import React, { useEffect, useState, useRef } from 'react'; // eslint-disable-line no-unused-vars
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '../../msalConfig';
import { useUser } from '../../contexts/UserContext';
import { logAccess } from '../../utils/userLog';

/**
 * 인증이 필요한 라우트를 보호하는 컴포넌트
 * 로그인되지 않은 사용자에게 로그인 페이지를 표시
 */
// Favicon 복원 함수 (MSAL 리다이렉트 후 Microsoft favicon이 남는 문제 해결)
const FAVICON_SVG = "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><rect width='100' height='100' rx='20' fill='%23ccc5b9'/><text x='50' y='68' font-size='50' text-anchor='middle' fill='%233e3832' font-family='system-ui'>I</text></svg>";

const restoreFavicon = () => {
  // 기존 favicon 링크 찾기
  let link = document.querySelector("link[rel~='icon']");

  if (link) {
    link.href = FAVICON_SVG;
  } else {
    // favicon 링크가 없으면 새로 생성
    link = document.createElement('link');
    link.rel = 'icon';
    link.href = FAVICON_SVG;
    document.head.appendChild(link);
  }
};

const ProtectedRoute = ({ children }) => {
  const { instance, inProgress, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [isLoading, setIsLoading] = useState(true);
  const { fetchUserInfo, user } = useUser();
  const loggedRef = useRef(false);

  // 인증 완료 + 사용자 정보 로드 완료 시 접근 로그 기록 (1회만)
  useEffect(() => {
    if (isAuthenticated && user && !loggedRef.current) {
      loggedRef.current = true;
      const pathname = window.location.pathname;
      // matrix-board는 Files.js에서 activeBrdCd와 함께 로그 기록
      if (pathname.includes('/matrix-board')) return;
      const pageBrdMatch = pathname.match(/\/interstellar\/([^\/]+)\//);
      const page_brd_cd = pageBrdMatch ? pageBrdMatch[1] : null;
      logAccess({
        user: `${user.userId}(${user.name})`,
        email: user.email,
        brd_cd: user.brdCd || null,
        page_brd_cd,
        page_nm: pathname,
      });
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    // 인증 진행 중이 아니면 로딩 종료
    if (inProgress === InteractionStatus.None) {
      setIsLoading(false);
    }
  }, [inProgress]);

  // 인증 완료 후 favicon 복원 및 사용자 정보 로드
  useEffect(() => {
    if (isAuthenticated && inProgress === InteractionStatus.None && accounts.length > 0) {

      const account = accounts[0];
      // 약간의 딜레이 후 favicon 복원 (DOM 로딩 완료 보장)
      setTimeout(() => {
        restoreFavicon();
      }, 100);

      // Access Token 가져오기
      instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0]
      }).then((response) => {
        localStorage.setItem('authToken', response.accessToken);

        // 사용자 정보가 아직 로드되지 않았다면 로드
        if (!user) {
          fetchUserInfo();
        }
      }).catch((error) => {
        console.error('ProtectedRoute: Access Token 획득 실패:', error);

        // InteractionRequiredAuthError인 경우 리디렉트로 재시도
        if (error.errorCode === 'interaction_required' || error.errorCode === 'consent_required' || error.errorCode === 'login_required') {
          console.log('ProtectedRoute: 상호작용이 필요합니다. 리디렉트로 토큰 획득 시도...');
          instance.acquireTokenRedirect({
            ...loginRequest,
            account: accounts[0]
          }).catch((redirectError) => {
            console.error('ProtectedRoute: 리디렉트 토큰 획득 실패:', redirectError);
          });
        }
      });
    }
  }, [isAuthenticated, inProgress, accounts, user, fetchUserInfo, instance]);

  // 로딩 중일 때 표시할 화면
  if (isLoading || inProgress !== InteractionStatus.None) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
          backgroundColor: '#ccc5b9',
          flexDirection: 'column',
          gap: '24px',
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }}
      >
        {/* 타이틀 */}
        <h1
          style={{
            fontSize: '32px',
            fontWeight: 700,
            color: '#3e3832',
            margin: 0,
            marginBottom: '16px'
          }}
        >
          MatrixBoard
        </h1>

        {/* 사용자 아이콘 */}
        <div
          style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: '#3e3832',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '8px'
          }}
        >
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"
              fill="#ccc5b9"
            />
          </svg>
        </div>

        {/* 로딩 스피너 */}
        <div
          style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e5dfd7',
            borderTop: '4px solid #3e3832',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}
        />

        {/* 로딩 메시지 */}
        <p
          style={{
            fontSize: '16px',
            color: '#3e3832',
            textAlign: 'center',
            margin: 0,
            fontWeight: 500
          }}
        >
          Microsoft 로그인 진행 중...
        </p>

        {/* 애니메이션 정의 */}
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // 인증되지 않았으면 로그인 안내 화면
  if (!isAuthenticated) {
    const handleLogin = async () => {
      // 이미 인증 진행 중이면 무시
      if (inProgress !== InteractionStatus.None) {
        console.log("로그인이 이미 진행 중입니다.");
        return;
      }

      try {
        // 리디렉트 방식 사용 (팝업 차단 방지)
        await instance.loginRedirect(loginRequest);
      } catch (error) {
        console.error("로그인 실패:", error);
      }
    };

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
          backgroundColor: '#ccc5b9',
          flexDirection: 'column',
          gap: '24px',
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }}
      >
        {/* 타이틀 */}
        <h1
          style={{
            fontSize: '32px',
            fontWeight: 700,
            color: '#3e3832',
            margin: 0,
            marginBottom: '16px'
          }}
        >
          MatrixBoard
        </h1>

        {/* 사용자 아이콘 */}
        <div
          style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: '#3e3832',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '8px'
          }}
        >
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"
              fill="#ccc5b9"
            />
          </svg>
        </div>

        {/* 안내 메시지 */}
        {/* <p
          style={{
            fontSize: '14px',
            color: '#3e3832',
            textAlign: 'center',
            margin: 0,
            marginBottom: '8px'
          }}
        >
          로그인 후 사용해주세요. 권한이 필요한 경우 AI 엔지니어링팀 엄경호에게 문의해주세요.
        </p> */}

        {/* SSO 로그인 버튼 */}
        <button
          onClick={handleLogin}
          disabled={inProgress !== InteractionStatus.None}
          style={{
            padding: '12px 80px',
            backgroundColor: '#3e3832',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: inProgress !== InteractionStatus.None ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            fontWeight: 500,
            opacity: inProgress !== InteractionStatus.None ? 0.7 : 1,
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            if (inProgress === InteractionStatus.None) {
              e.currentTarget.style.backgroundColor = '#2c2824';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#3e3832';
          }}
        >
          {inProgress !== InteractionStatus.None ? '로그인 진행 중...' : 'SSO 로그인'}
        </button>
      </div>
    );
  }

  // 인증되었으면 자식 컴포넌트 렌더링
  return <>{children}</>;
};

export default ProtectedRoute;
